import { useEffect, useMemo, useRef, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import { Checkbox, CheckboxGroup, Input, InputNumber, Loader, Message, SelectPicker, Tooltip, Whisper, toaster } from 'rsuite';
import { Button as NsiButton, Modal, useLastPresent } from '@nsi-advisory-services/components';

import {
  useCloseDates,
  useCreateTaskOrderScenario,
  useDeleteTaskOrderScenario,
  useEmailScenarioLead,
  useScenarioContext,
  useTaskOrderScenario,
  useTaskOrderScenarios,
  useUpdateTaskOrderScenario,
  useUsers,
  useUsersTimeOffDays,
} from '../hooks/useQueries';
import { buildTimeOffHoursByDay, buildWorkingDays, fteScaleOf, monthCapacity } from '../utils/capacity';
import {
  FlashFillButton,
  FlashFillGhost,
  buildFlashFillCellRuns,
  describeFlashFill,
  getFlashFillRunKey,
  isFlashFillShortcut,
  uniqueFlashFillRuns,
  useFlashFillUndo,
} from './FlashFill';
import { evaluateSum } from '../utils/arithmetic';
import './StaffingScenarios.css';

/*
 * Staffing scenarios: the PM's what-if for a task order. Pick a period, set how many
 * hours each person gives the task order each month, and see how that lands against
 * the team's availability and a spend target. Nothing here changes the forecast; the
 * discipline leads own that. What comes out is a list of instructions for the leads:
 * who needs more or fewer hours on which discipline, and when.
 *
 * Availability is capacity (working days, close dates, time off, FTE) less everything
 * the person is forecast for outside this task order, worked out one month at a time.
 * Months are never pooled: a vacation in November is not covered by spare time in
 * December, and being over in one month does not use up room in another.
 */

const MAX_MONTHS = 36;

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const currencyCompact = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 });
const formatMoney = (v) => (Number.isFinite(Number(v)) ? currency.format(Number(v)) : '—');
const formatMoneyCompact = (v) => (Number.isFinite(Number(v)) ? currencyCompact.format(Number(v)) : '—');
const formatHours = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
};
const signed = (v) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${formatHours(Math.abs(v))}`;

// Free time is rounded down to the quarter hour so it never promises time that isn't there; the
// epsilon keeps float noise (80.7499999) from dropping a whole quarter.
const floorQuarter = (v) => Math.floor(Number(v) * 4 + 1e-9) / 4;
const formatQuarterHours = (v) => (Number.isFinite(v) ? v.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—');
const formatFreeHours = (free) => formatQuarterHours(Math.max(0, floorQuarter(free)));
// The other side of the same rounding: an overage is rounded up to the quarter hour.
const formatOverHours = (free) => formatQuarterHours(-floorQuarter(free));

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad2 = (n) => String(n).padStart(2, '0');
const addMonths = (ms, delta) => {
  const [y, m] = String(ms).split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-01`;
};
const monthsBetween = (start, end) => {
  const out = [];
  if (!start || !end) return out;
  for (let i = 0; i < MAX_MONTHS; i += 1) {
    const ms = addMonths(start, i);
    if (ms > end) break;
    out.push(ms);
  }
  return out;
};
const monthShort = (ms) => {
  const [y, m] = String(ms || '').split('-').map(Number);
  return Number.isInteger(y) && Number.isInteger(m) ? `${MONTHS_SHORT[m - 1]} '${String(y).slice(-2)}` : '—';
};
const monthName = (ms) => {
  const [y, m] = String(ms || '').split('-').map(Number);
  return Number.isInteger(y) && Number.isInteger(m) ? new Date(y, m - 1, 1).toLocaleString(undefined, { month: 'long' }) : '—';
};
const periodLabel = (start, end) => (start === end ? monthShort(start) : `${monthShort(start)} – ${monthShort(end)}`);

const roundHalf = (v) => Math.round(Number(v) * 2) / 2;

const errorText = (e) => {
  const raw = String(e?.message || e || '').replace(/^HTTP \d+:\s*/, '');
  try {
    return JSON.parse(raw)?.error || raw;
  } catch {
    return raw;
  }
};

const toDraft = (s) => ({
  id: s.id,
  name: s.name,
  startMonth: s.startMonth,
  endMonth: s.endMonth,
  budgetTarget: s.budgetTarget,
  people: Array.isArray(s.people) ? s.people : [],
  hours: s.hours && typeof s.hours === 'object' ? s.hours : {},
  notes: s.notes || '',
});

// Stable form of a draft for "has anything changed" checks: zero cells and key order
// must not count as edits.
const fingerprint = (d) => {
  if (!d) return '';
  const hours = {};
  for (const uid of Object.keys(d.hours || {}).sort()) {
    const months = {};
    for (const ms of Object.keys(d.hours[uid] || {}).sort()) {
      const h = Number(d.hours[uid][ms]);
      if (h > 0) months[ms] = h;
    }
    if (Object.keys(months).length) hours[uid] = months;
  }
  return JSON.stringify({
    name: d.name,
    startMonth: d.startMonth,
    endMonth: d.endMonth,
    budgetTarget: d.budgetTarget ?? null,
    people: [...(d.people || [])].map(Number).sort((a, b) => a - b),
    hours,
    notes: d.notes || '',
  });
};

// Spread a change in someone's monthly hours across disciplines in proportion to
// weights, rounded to half hours, with the rounding remainder on the largest share so
// the parts still add up to the change.
const splitAcross = (delta, weights) => {
  const list = weights.filter((w) => w.weight > 0);
  const total = list.reduce((acc, w) => acc + w.weight, 0);
  if (total <= 0) return [{ taskId: null, hours: roundHalf(delta) }];
  const parts = list.map((w) => ({ taskId: w.taskId, weight: w.weight, hours: roundHalf((delta * w.weight) / total) }));
  const diff = roundHalf(delta) - parts.reduce((acc, p) => acc + p.hours, 0);
  if (Math.abs(diff) > 1e-9) {
    const biggest = parts.reduce((best, p) => (p.weight > best.weight ? p : best), parts[0]);
    biggest.hours += diff;
  }
  return parts.filter((p) => Math.abs(p.hours) >= 0.5);
};

// Differences between the scenario and the current forecast, as per person, per
// discipline changes by month. Reductions come out of the disciplines the person is
// on that month, in proportion. Additions follow the same mix; in a month where they
// have nothing yet, their mix across the whole task order; failing that, the
// disciplines they are rostered on; failing that, they are left for the PM to place.
const buildInstructions = (rows, months) => {
  const byKey = new Map();
  for (const row of rows) {
    for (const ms of months) {
      const c = row.cells[ms];
      const delta = c.scenario - c.current;
      if (Math.abs(delta) < 0.5) continue;

      let weights = Object.entries(c.byTask).map(([taskId, h]) => ({ taskId: Number(taskId), weight: h }));
      if (delta > 0 && !weights.some((w) => w.weight > 0)) {
        weights = Object.entries(row.mixByTask || {}).map(([taskId, h]) => ({ taskId: Number(taskId), weight: Number(h) }));
        if (!weights.some((w) => w.weight > 0)) {
          weights = (row.rosterTaskIds || []).map((taskId) => ({ taskId: Number(taskId), weight: 1 }));
        }
      }

      for (const part of splitAcross(delta, weights)) {
        const key = `${row.userId}|${part.taskId ?? 'none'}`;
        if (!byKey.has(key)) byKey.set(key, { userId: row.userId, name: row.name, taskId: part.taskId, months: [] });
        byKey.get(key).months.push({ monthStart: ms, hours: part.hours });
      }
    }
  }
  return Array.from(byKey.values()).map((item) => ({
    ...item,
    net: item.months.reduce((acc, m) => acc + m.hours, 0),
    added: item.months.reduce((acc, m) => acc + Math.max(0, m.hours), 0),
    removed: item.months.reduce((acc, m) => acc + Math.max(0, -m.hours), 0),
  }));
};

const describeInstruction = (item, disciplineName) => {
  const first = item.months[0].monthStart;
  const last = item.months[item.months.length - 1].monthStart;
  const span = item.months.length === 1
    ? `in ${monthName(first)}`
    : `over ${item.months.length} months (${periodLabel(first, last)})`;
  const allUp = item.months.every((m) => m.hours > 0);
  const allDown = item.months.every((m) => m.hours < 0);

  if (item.taskId === null) {
    return `${item.name} needs ${formatHours(item.net)} more hours on this task order ${span}. They have no discipline here yet, so choose one.`;
  }
  if (allUp) return `${item.name} needs ${formatHours(item.net)} more hours on ${disciplineName} ${span}.`;
  if (allDown) return `${item.name} needs ${formatHours(-item.net)} hours reduced from ${disciplineName} ${span}.`;
  return `${item.name}’s hours on ${disciplineName} need moving between months, ${periodLabel(first, last)} (net ${signed(item.net)} h).`;
};

const monthDetail = (item) => item.months.map((m) => `${monthShort(m.monthStart)} ${signed(m.hours)}`).join(' · ');

// Name, period and notes. Used for both creating a scenario and editing its details.
// An hours cell that also takes a quick sum ("1+4", "40-8"). While focused it keeps exactly what was
// typed, passing the result on whenever the text is a complete sum so totals update as you type; on
// blur or Enter the text is replaced by the answer. An unfinished sum ("1+") leaves the last complete
// one in place, and Escape restores the value the cell had when it was focused.
function SumInput({ value, onCommit, onKeyDown, ...rest }) {
  const [text, setText] = useState(null);
  const valueAtFocusRef = useRef(value);

  const finish = () => {
    if (text === null) return;
    if (text.trim() === '') onCommit('');
    else {
      const sum = evaluateSum(text);
      if (sum !== null) onCommit(Math.round(sum * 100) / 100);
    }
    setText(null);
  };

  return (
    <input
      {...rest}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={text ?? (value ? String(value) : '')}
      onChange={(e) => {
        const next = e.target.value;
        setText(next);
        if (next.trim() === '') onCommit('');
        else {
          const sum = evaluateSum(next);
          if (sum !== null) onCommit(Math.round(sum * 100) / 100);
        }
      }}
      onFocus={() => {
        valueAtFocusRef.current = value;
      }}
      onBlur={finish}
      onKeyDown={(e) => {
        if (e.key === 'Enter') finish();
        else if (e.key === 'Escape') {
          onCommit(valueAtFocusRef.current || '');
          setText(null);
        }
        onKeyDown?.(e);
      }}
    />
  );
}

function ScenarioDetailsModal({ open, title, initial, monthOptions, onCancel, onSubmit, pending, error, periodWarning }) {
  const [name, setName] = useState('');
  const [startMonth, setStartMonth] = useState(null);
  const [endMonth, setEndMonth] = useState(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(initial?.name || '');
    setStartMonth(initial?.startMonth || null);
    setEndMonth(initial?.endMonth || null);
    setNotes(initial?.notes || '');
  }, [initial, open]);

  const endOptions = monthOptions.filter((o) => startMonth && o.value >= startMonth && o.value <= addMonths(startMonth, MAX_MONTHS - 1));
  const valid = name.trim() && startMonth && endMonth && endMonth >= startMonth;
  const count = valid ? monthsBetween(startMonth, endMonth).length : 0;
  const droppingMonths = periodWarning && valid && (startMonth > periodWarning.startMonth || endMonth < periodWarning.endMonth);

  return (
    <Modal open={open} onClose={() => !pending && onCancel()} size="sm">
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="ppc-sc-form">
          <div className="ppc-sc-form__label">Name</div>
          <Input value={name} onChange={setName} placeholder="e.g. Use the budget by June" maxLength={120} />

          <div className="ppc-sc-form__row">
            <div>
              <div className="ppc-sc-form__label">First month</div>
              <SelectPicker
                block
                cleanable={false}
                searchable={false}
                data={monthOptions}
                value={startMonth}
                onChange={(v) => {
                  setStartMonth(v);
                  if (v && (!endMonth || endMonth < v || endMonth > addMonths(v, MAX_MONTHS - 1))) setEndMonth(v);
                }}
              />
            </div>
            <div>
              <div className="ppc-sc-form__label">Last month</div>
              <SelectPicker block cleanable={false} searchable={false} data={endOptions} value={endMonth} onChange={setEndMonth} />
            </div>
          </div>
          <div className="ppc-sc-form__hint">
            {count > 0 ? `${count} month${count === 1 ? '' : 's'}. ` : ''}
            Availability and the spend target are worked out for this period.
          </div>
          {droppingMonths ? (
            <Message type="warning" showIcon>Hours entered for months outside the new period will be removed when you save.</Message>
          ) : null}

          <div className="ppc-sc-form__label">Notes (optional)</div>
          <Input as="textarea" rows={3} value={notes} onChange={setNotes} placeholder="What this scenario is trying out" />

          {error ? <Message type="error" showIcon>{errorText(error)}</Message> : null}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <NsiButton onClick={onCancel} disabled={pending}>Cancel</NsiButton>
        <NsiButton
          variant="primary"
          loading={pending}
          disabled={!valid || pending}
          onClick={() => onSubmit({ name: name.trim(), startMonth, endMonth, notes })}
        >
          {initial?.id ? 'Save details' : 'Create scenario'}
        </NsiButton>
      </Modal.Footer>
    </Modal>
  );
}

// Who a discipline's instructions go to by default: its own lead, else its activity leads, else the PIC.
const defaultEmailRecipients = (contacts) => {
  for (const role of ['Discipline lead', 'Activity lead', 'PIC']) {
    const ids = contacts.filter((c) => c.role === role).map((c) => c.userId);
    if (ids.length) return ids;
  }
  return [];
};

// Compose an email of instructions to a discipline's lead(s). email: { disciplineName, contacts,
// subject, buildMessage(recipientNames) } or null when closed.
function EmailLeadModal({ email, onCancel, onSend, pending, error }) {
  const emailShown = useLastPresent(email); // keeps the dialog steady while it fades out
  const [recipientIds, setRecipientIds] = useState([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [ccSelf, setCcSelf] = useState(true);

  useEffect(() => {
    if (!email) return;
    const ids = defaultEmailRecipients(email.contacts);
    setRecipientIds(ids);
    setSubject(email.subject);
    setMessage(email.buildMessage(email.contacts.filter((c) => ids.includes(c.userId)).map((c) => c.name)));
    setCcSelf(true);
  }, [email]);

  const valid = recipientIds.length > 0 && subject.trim() && message.trim();

  return (
    <Modal open={!!email} onClose={() => !pending && onCancel()} size="md">
      <Modal.Header>
        <Modal.Title>Email {emailShown?.disciplineName} lead</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="ppc-sc-form">
          <div className="ppc-sc-form__label">To</div>
          <CheckboxGroup value={recipientIds} onChange={(v) => setRecipientIds(v.map(Number))} className="ppc-sc-email__to">
            {(emailShown?.contacts || []).map((c) => (
              <Checkbox key={c.userId} value={c.userId}>
                {c.name} <span className="ppc-sc-email__role">· {c.role}</span>
              </Checkbox>
            ))}
          </CheckboxGroup>

          <div className="ppc-sc-form__label">Subject</div>
          <Input value={subject} onChange={setSubject} maxLength={200} />

          <div className="ppc-sc-form__label">Message</div>
          <Input as="textarea" rows={12} value={message} onChange={setMessage} />

          <Checkbox checked={ccSelf} onChange={(_, checked) => setCcSelf(checked)}>Send me a copy</Checkbox>
          <div className="ppc-sc-form__hint">Sent from ProPlanConnect&apos;s no-reply address; replies come to you.</div>

          {error ? <Message type="error" showIcon>{errorText(error)}</Message> : null}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <NsiButton onClick={onCancel} disabled={pending}>Cancel</NsiButton>
        <NsiButton
          variant="primary"
          loading={pending}
          disabled={!valid || pending}
          onClick={() => onSend({ recipientUserIds: recipientIds, subject: subject.trim(), message: message.trim(), ccSelf })}
        >
          Send
        </NsiButton>
      </Modal.Footer>
    </Modal>
  );
}

// Shown when leaving the page with unsaved scenario changes.
function LeaveUnsavedModal({ open, saving, onSaveAndLeave, onLeave, onStay }) {
  return (
    <Modal open={open} onClose={saving ? undefined : onStay} size="xs">
      <Modal.Header>
        <Modal.Title>Leave with unsaved changes?</Modal.Title>
      </Modal.Header>
      <Modal.Body>This scenario has changes that haven&rsquo;t been saved. Save them before you go?</Modal.Body>
      <Modal.Footer>
        <NsiButton variant="danger" onClick={onLeave} disabled={saving}>Leave without saving</NsiButton>
        <NsiButton onClick={onStay} disabled={saving}>Stay</NsiButton>
        <NsiButton variant="primary" loading={saving} onClick={onSaveAndLeave}>Save and leave</NsiButton>
      </Modal.Footer>
    </Modal>
  );
}

function ConfirmModal({ confirm, onClose }) {
  const confirmShown = useLastPresent(confirm); // keeps the dialog steady while it fades out
  return (
    <Modal open={!!confirm} onClose={onClose} size="xs">
      <Modal.Header>
        <Modal.Title>{confirmShown?.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{confirmShown?.body}</Modal.Body>
      <Modal.Footer>
        <NsiButton onClick={onClose} disabled={confirmShown?.pending}>Cancel</NsiButton>
        <NsiButton
          variant="primary"
          color={confirmShown?.danger ? 'red' : undefined}
          loading={confirmShown?.pending}
          onClick={confirmShown?.onConfirm}
        >
          {confirmShown?.confirmLabel || 'Confirm'}
        </NsiButton>
      </Modal.Footer>
    </Modal>
  );
}

export default function StaffingScenarios({
  projectId,
  taskOrderId,
  currentMonth,
  taskOrderEndMonth,
  budget,
  spentToDate,
  outlookMonths,
  outlookDollarsByMonth,
  odcDollarsByMonth,
}) {
  const scenariosQuery = useTaskOrderScenarios(projectId, taskOrderId);
  const configured = scenariosQuery.data?.configured !== false;
  const scenarios = useMemo(() => scenariosQuery.data?.scenarios || [], [scenariosQuery.data?.scenarios]);

  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [detailsMode, setDetailsMode] = useState(null); // 'create' | 'edit' | null
  const [confirm, setConfirm] = useState(null);
  const [groupBy, setGroupBy] = useState('discipline');

  const createScenario = useCreateTaskOrderScenario();
  const updateScenario = useUpdateTaskOrderScenario();
  const deleteScenario = useDeleteTaskOrderScenario();

  // Open the most recently changed scenario by default.
  useEffect(() => {
    if (selectedId !== null || scenarios.length === 0) return;
    setSelectedId(scenarios[0].id);
  }, [scenarios, selectedId]);

  const scenarioQuery = useTaskOrderScenario(projectId, taskOrderId, selectedId);
  const saved = scenarioQuery.data;

  const { recordFlashFill, clearFlashFillUndo } = useFlashFillUndo();

  // Reset the draft whenever a different scenario (or a newly saved version) arrives.
  useEffect(() => {
    if (saved) setDraft(toDraft(saved));
    clearFlashFillUndo();
  }, [saved?.id, saved?.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty = !!draft && !!saved && draft.id === saved.id && fingerprint(draft) !== fingerprint(toDraft(saved));

  // Unsaved changes hold any in-app navigation away (links, back/forward, navigate()) until the
  // user picks Save, Leave or Stay in LeaveUnsavedModal.
  const leaveBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty &&
      (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search)
  );
  const [savingToLeave, setSavingToLeave] = useState(false);

  // Closing or reloading the tab gets the browser's own "leave site?" prompt instead.
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      // Chrome requires returnValue to be set.
      // eslint-disable-next-line no-param-reassign
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const months = useMemo(() => monthsBetween(draft?.startMonth, draft?.endMonth), [draft?.endMonth, draft?.startMonth]);

  const contextQuery = useScenarioContext(projectId, taskOrderId, draft?.startMonth, draft?.endMonth, draft?.people, {
    enabled: !!draft,
  });
  const context = contextQuery.data;
  const contextUsers = useMemo(() => (Array.isArray(context?.users) ? context.users : []), [context?.users]);
  const disciplines = useMemo(() => (Array.isArray(context?.disciplines) ? context.disciplines : []), [context?.disciplines]);
  const disciplineById = useMemo(() => new Map(disciplines.map((d) => [d.taskId, d])), [disciplines]);

  const userIds = useMemo(() => contextUsers.map((u) => u.userId), [contextUsers]);
  const timeOffQueries = useUsersTimeOffDays(userIds, { enabled: userIds.length > 0 });
  const { data: closeDatesRes } = useCloseDates();
  const { data: usersRes } = useUsers();

  const closeDates = useMemo(() => {
    const list = Array.isArray(closeDatesRes) ? closeDatesRes : closeDatesRes?.rows;
    return Array.isArray(list) ? list : [];
  }, [closeDatesRes]);
  const workingDaysByMonth = useMemo(() => {
    const map = {};
    for (const ms of months) map[ms] = buildWorkingDays(ms, closeDates);
    return map;
  }, [closeDates, months]);

  const timeOffKey = timeOffQueries.map((q) => q?.dataUpdatedAt).join(',');

  // One row per person with, for every month: capacity, other work, room, the current
  // forecast (and its split by discipline) and the scenario's hours.
  const rows = useMemo(() => {
    if (!draft) return [];
    return contextUsers.map((u, idx) => {
      const timeOffByDay = buildTimeOffHoursByDay(timeOffQueries[idx]?.data);
      const fte = fteScaleOf(u);
      const cells = {};
      let scenarioHours = 0;
      let scenarioDollars = 0;
      let currentHours = 0;
      for (const ms of months) {
        const { capacity, timeOff } = monthCapacity(workingDaysByMonth[ms] || [], timeOffByDay, fte);
        const other = Number(u.otherHoursByMonth?.[ms] ?? 0);
        const room = Math.max(0, capacity - other);
        const byTask = u.forecastByMonth?.[ms] || {};
        const current = Object.values(byTask).reduce((acc, h) => acc + Number(h || 0), 0);
        const scenario = Number(draft.hours?.[u.userId]?.[ms] ?? 0) || 0;
        const rate = Number(u.rateByMonth?.[ms] ?? 0);
        cells[ms] = { capacity, timeOff, other, room, byTask, current, scenario, rate };
        scenarioHours += scenario;
        scenarioDollars += scenario * rate;
        currentHours += current;
      }
      const onTaskOrder = currentHours > 0 || Object.keys(u.mixByTask || {}).length > 0;
      return {
        ...u,
        cells,
        scenarioHours,
        scenarioDollars,
        currentHours,
        removable: !onTaskOrder && (draft.people || []).includes(u.userId),
      };
    });
  }, [contextUsers, draft, months, timeOffKey, workingDaysByMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => {
    const byMonth = {};
    let room = 0;
    let scenarioHours = 0;
    let scenarioLabor = 0;
    let unused = 0;
    let over = 0;
    let currentHours = 0;
    const overCells = [];
    for (const ms of months) byMonth[ms] = { room: 0, scenario: 0, current: 0, over: 0 };
    for (const r of rows) {
      for (const ms of months) {
        const c = r.cells[ms];
        byMonth[ms].room += c.room;
        byMonth[ms].scenario += c.scenario;
        byMonth[ms].current += c.current;
        // Per cell, never netted: room left in one month or for one person does not
        // offset someone being over somewhere else.
        const cellOver = Math.max(0, c.scenario - c.room);
        byMonth[ms].over += cellOver;
        if (cellOver > 0.01) overCells.push({ name: r.name, monthStart: ms, over: cellOver });
        unused += Math.max(0, c.room - c.scenario);
        over += cellOver;
      }
      room += months.reduce((acc, ms) => acc + r.cells[ms].room, 0);
      scenarioHours += r.scenarioHours;
      scenarioLabor += r.scenarioDollars;
      currentHours += r.currentHours;
    }
    const odc = months.reduce((acc, ms) => acc + Number(odcDollarsByMonth?.[ms] ?? 0), 0);

    // Blended rate: the scenario's own dollars over its hours; before any hours are
    // entered, everyone's rate weighted by the room they have.
    let weightedRate = 0;
    let weight = 0;
    for (const r of rows) {
      for (const ms of months) {
        weightedRate += r.cells[ms].rate * r.cells[ms].room;
        weight += r.cells[ms].room;
      }
    }
    const blendedRate = scenarioHours > 0 ? scenarioLabor / scenarioHours : weight > 0 ? weightedRate / weight : null;

    return { byMonth, room, scenarioHours, scenarioLabor, odc, spend: scenarioLabor + odc, unused, over, overCells, currentHours, blendedRate };
  }, [months, odcDollarsByMonth, rows]);

  // Straight-line default: the budget left, spread evenly over the months the task order
  // has left, times the months of this period that fall inside it.
  const defaultTarget = useMemo(() => {
    if (budget === null || budget === undefined || !months.length) return null;
    const lastMonth = taskOrderEndMonth || outlookMonths[outlookMonths.length - 1] || months[months.length - 1];
    const remaining = monthsBetween(currentMonth, lastMonth);
    if (!remaining.length) return null;
    const inPeriod = months.filter((ms) => ms >= currentMonth && ms <= lastMonth).length;
    return Math.max(0, budget - spentToDate) * (inPeriod / remaining.length);
  }, [budget, currentMonth, months, outlookMonths, spentToDate, taskOrderEndMonth]);

  const target = draft?.budgetTarget ?? defaultTarget;
  const gapDollars = target !== null && target !== undefined ? target - totals.spend : null;
  const hoursToTarget = gapDollars !== null && totals.blendedRate ? gapDollars / totals.blendedRate : null;

  // Where the whole task order would finish if this scenario happened: the outlook for
  // every month outside the period, plus the scenario (and ODC forecast) inside it.
  const finishTotal = useMemo(() => {
    const inPeriod = new Set(months);
    const outside = (outlookMonths || []).reduce(
      (acc, ms) => acc + (inPeriod.has(ms) ? 0 : Number(outlookDollarsByMonth?.[ms] ?? 0)),
      0
    );
    return outside + totals.spend;
  }, [months, outlookDollarsByMonth, outlookMonths, totals.spend]);

  const instructions = useMemo(() => buildInstructions(rows, months), [months, rows]);

  const monthOptions = useMemo(
    () => Array.from({ length: MAX_MONTHS }, (_, i) => addMonths(currentMonth, i)).map((ms) => ({ label: monthShort(ms), value: ms })),
    [currentMonth]
  );

  const allUsers = useMemo(() => {
    const list = Array.isArray(usersRes) ? usersRes : usersRes?.users;
    return (Array.isArray(list) ? list : [])
      .map((u) => ({ id: Number(u?.id), name: String(u?.name || '').trim() }))
      .filter((u) => Number.isInteger(u.id) && u.name)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [usersRes]);
  const addablePeople = useMemo(() => {
    const shown = new Set(rows.map((r) => r.userId));
    return allUsers.filter((u) => !shown.has(u.id)).map((u) => ({ label: u.name, value: u.id }));
  }, [allUsers, rows]);

  // ---- editing -----------------------------------------------------------------

  const setCell = (userId, ms, raw) => {
    const value = raw === '' ? 0 : Math.max(0, Math.min(744, Number(raw)));
    if (!Number.isFinite(value)) return;
    setDraft((d) => {
      const hours = { ...d.hours, [userId]: { ...(d.hours[userId] || {}) } };
      if (value > 0) hours[userId][ms] = value;
      else delete hours[userId][ms];
      return { ...d, hours };
    });
  };

  const flashFillRunsByUserId = useMemo(
    () => buildFlashFillCellRuns(rows, months, (r, ms) => r.cells[ms]?.scenario ?? 0),
    [months, rows]
  );

  // Runs only ever fill empty months, so undoing one just clears them again.
  const applyFlashFill = (row, runs) => {
    if (runs.length === 0) return;
    const { userId } = row;
    const draftId = draft?.id;

    setDraft((d) => {
      const hours = { ...d.hours, [userId]: { ...(d.hours[userId] || {}) } };
      for (const run of runs) {
        for (const ms of run.months) hours[userId][ms] = run.value;
      }
      return { ...d, hours };
    });

    recordFlashFill({
      message: describeFlashFill(row.name, runs),
      undo: () => setDraft((d) => {
        if (!d || d.id !== draftId) return d;
        const userHours = { ...(d.hours[userId] || {}) };
        for (const run of runs) {
          for (const ms of run.months) {
            // Leave cells the user has edited since the fill.
            if (Number(userHours[ms] ?? 0) === run.value) delete userHours[ms];
          }
        }
        return { ...d, hours: { ...d.hours, [userId]: userHours } };
      }),
    });
  };

  const insertCurrentForecast = () => {
    const hours = {};
    for (const r of rows) {
      for (const ms of months) {
        const h = roundHalf(r.cells[ms].current);
        if (h > 0) {
          if (!hours[r.userId]) hours[r.userId] = {};
          hours[r.userId][ms] = h;
        }
      }
    }
    setDraft((d) => ({ ...d, hours }));
    setConfirm(null);
  };

  const requestInsert = () => {
    if (totals.scenarioHours > 0) {
      setConfirm({
        title: 'Replace scenario hours?',
        body: 'This replaces every hour in the scenario with the current forecast for this period.',
        confirmLabel: 'Replace with forecast',
        onConfirm: insertCurrentForecast,
      });
    } else {
      insertCurrentForecast();
    }
  };

  const requestClear = () => {
    setConfirm({
      title: 'Clear all hours?',
      body: 'Every person goes back to 0 hours in this scenario. Nothing is saved until you click Save.',
      confirmLabel: 'Clear hours',
      onConfirm: () => {
        setDraft((d) => ({ ...d, hours: {} }));
        setConfirm(null);
      },
    });
  };

  const addPerson = (userId) => {
    if (!Number.isInteger(userId)) return;
    setDraft((d) => ({ ...d, people: Array.from(new Set([...(d.people || []), userId])) }));
  };

  const removePerson = (userId) => {
    setDraft((d) => {
      const hours = { ...d.hours };
      delete hours[userId];
      return { ...d, people: (d.people || []).filter((id) => id !== userId), hours };
    });
  };

  // Resolves true once saved; failures are reported in a toast and resolve false.
  const save = async () => {
    try {
      await updateScenario.mutateAsync({
        projectId,
        taskOrderId,
        scenarioId: draft.id,
        name: draft.name,
        startMonth: draft.startMonth,
        endMonth: draft.endMonth,
        budgetTarget: draft.budgetTarget ?? null,
        people: draft.people,
        hours: draft.hours,
        notes: draft.notes || null,
      });
      toaster.push(<Message type="success" showIcon>Scenario saved.</Message>, { placement: 'topEnd', duration: 3000 });
      return true;
    } catch (e) {
      toaster.push(<Message type="error" showIcon>{errorText(e)}</Message>, { placement: 'topEnd', duration: 6000 });
      return false;
    }
  };

  const saveAndLeave = async () => {
    setSavingToLeave(true);
    const ok = await save();
    setSavingToLeave(false);
    // On failure the dialog stays open (the toast says why) so the user can retry, leave or stay.
    if (ok) leaveBlocker.proceed?.();
  };

  const discard = () => {
    if (saved) setDraft(toDraft(saved));
    clearFlashFillUndo();
  };

  const submitDetails = async (values) => {
    if (detailsMode === 'create') {
      try {
        const created = await createScenario.mutateAsync({ projectId, taskOrderId, ...values, people: [], hours: {} });
        setSelectedId(created.id);
        setDetailsMode(null);
      } catch {
        // shown in the modal
      }
      return;
    }
    // Editing details changes the draft; months outside the new period drop out.
    setDraft((d) => {
      const hours = {};
      for (const [uid, byMonth] of Object.entries(d.hours || {})) {
        for (const [ms, h] of Object.entries(byMonth || {})) {
          if (ms < values.startMonth || ms > values.endMonth) continue;
          if (!hours[uid]) hours[uid] = {};
          hours[uid][ms] = h;
        }
      }
      return { ...d, ...values, hours };
    });
    setDetailsMode(null);
  };

  const requestDelete = () => {
    setConfirm({
      title: 'Delete scenario?',
      body: `“${saved?.name}” will be deleted for everyone. The forecast is not affected.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        setConfirm((c) => ({ ...c, pending: true }));
        try {
          await deleteScenario.mutateAsync({ projectId, taskOrderId, scenarioId: saved.id });
          setSelectedId(null);
          setDraft(null);
          setConfirm(null);
        } catch (e) {
          setConfirm(null);
          toaster.push(<Message type="error" showIcon>{errorText(e)}</Message>, { placement: 'topEnd', duration: 6000 });
        }
      },
    });
  };

  const newScenarioDefaults = useMemo(() => {
    const start = currentMonth;
    const end = taskOrderEndMonth && taskOrderEndMonth >= start
      ? (taskOrderEndMonth <= addMonths(start, MAX_MONTHS - 1) ? taskOrderEndMonth : addMonths(start, MAX_MONTHS - 1))
      : addMonths(start, 2);
    return { name: `Scenario ${scenarios.length + 1}`, startMonth: start, endMonth: end, notes: '' };
  }, [currentMonth, scenarios.length, taskOrderEndMonth]);

  // ---- instructions text ----------------------------------------------------------

  const disciplineLabel = (taskId) => disciplineById.get(taskId)?.name || 'Unknown discipline';
  const leadLabel = (taskId) => {
    const d = disciplineById.get(taskId);
    if (!d) return null;
    if (d.leadName) return d.leadName;
    if (d.activityLeadNames?.length) return d.activityLeadNames.join(', ');
    return null;
  };

  const instructionGroups = useMemo(() => {
    const groups = new Map();
    for (const item of instructions) {
      const key = groupBy === 'person' ? `p:${item.userId}` : `d:${item.taskId ?? 'none'}`;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          title: groupBy === 'person' ? item.name : item.taskId === null ? 'Not on a discipline yet' : disciplineLabel(item.taskId),
          lead: groupBy === 'person' || item.taskId === null ? null : leadLabel(item.taskId),
          items: [],
        });
      }
      groups.get(key).items.push(item);
    }
    const list = Array.from(groups.values());
    for (const g of list) g.items.sort((a, b) => a.name.localeCompare(b.name) || disciplineLabel(a.taskId).localeCompare(disciplineLabel(b.taskId)));
    return list.sort((a, b) => (a.key.endsWith(':none') ? 1 : b.key.endsWith(':none') ? -1 : a.title.localeCompare(b.title)));
  }, [groupBy, instructions]); // eslint-disable-line react-hooks/exhaustive-deps

  const instructionLine = (item) => `${describeInstruction(item, disciplineLabel(item.taskId))}  [${monthDetail(item)}]`;

  const copyText = async (text, doneLabel) => {
    try {
      await navigator.clipboard.writeText(text);
      toaster.push(<Message type="success" showIcon>{doneLabel}</Message>, { placement: 'topEnd', duration: 2500 });
    } catch {
      toaster.push(<Message type="error" showIcon>Could not copy to the clipboard.</Message>, { placement: 'topEnd', duration: 4000 });
    }
  };

  const copyInstructions = () => {
    const lines = [`${draft.name} (${periodLabel(draft.startMonth, draft.endMonth)})`, ''];
    for (const g of instructionGroups) {
      lines.push(g.lead ? `${g.title} (lead: ${g.lead})` : g.title);
      for (const item of g.items) lines.push(`  - ${instructionLine(item)}`);
      lines.push('');
    }
    copyText(lines.join('\n').trim(), 'Instructions copied.');
  };

  // ---- emailing a lead ----------------------------------------------------------------

  const emailLead = useEmailScenarioLead();
  const [emailRequest, setEmailRequest] = useState(null); // { taskId, items, ...EmailLeadModal email }

  const emailContactsFor = (taskId) => (taskId === null ? [] : disciplineById.get(taskId)?.contacts || []);

  const openEmail = (taskId, items) => {
    const disciplineName = disciplineLabel(taskId);
    const period = periodLabel(draft.startMonth, draft.endMonth);
    emailLead.reset();
    setEmailRequest({
      taskId,
      disciplineName,
      contacts: emailContactsFor(taskId),
      subject: `Staffing changes for ${disciplineName}: ${draft.name}`,
      buildMessage: (names) => [
        `Hi ${names.length ? names.map((n) => n.split(' ')[0]).join(', ') : 'there'},`,
        '',
        `The "${draft.name}" staffing scenario (${period}) needs these changes on ${disciplineName}:`,
        '',
        ...items.map((item) => `- ${instructionLine(item)}`),
        '',
        'Could you update the forecast to match? Thanks!',
      ].join('\n'),
    });
  };

  const sendEmail = async (body) => {
    try {
      const res = await emailLead.mutateAsync({ projectId, taskOrderId, scenarioId: draft.id, taskId: emailRequest.taskId, ...body });
      const names = (res?.to || []).map((p) => p.name).join(', ');
      toaster.push(<Message type="success" showIcon>Email sent to {names || 'the lead'}.</Message>, { placement: 'topEnd', duration: 3000 });
      setEmailRequest(null);
    } catch {
      // Shown in the modal via emailLead.error.
    }
  };

  // ---- render -----------------------------------------------------------------------

  const barMax = Math.max(1, ...months.map((ms) => Math.max(totals.byMonth[ms]?.room || 0, totals.byMonth[ms]?.scenario || 0)));
  const addedTotal = instructions.reduce((acc, i) => acc + i.added, 0);
  const removedTotal = instructions.reduce((acc, i) => acc + i.removed, 0);
  const instructionPeople = new Set(instructions.map((i) => i.userId)).size;

  return (
    <section className="ppc-tod-card ppc-sc">
      <div className="ppc-tod-card__head">
        <h2 className="ppc-tod-card__title">Scenarios</h2>
        {configured ? (
          <div className="ppc-tod-card__tools">
            {scenarios.length > 0 ? (
              <Whisper
                trigger={isDirty ? 'hover' : 'none'}
                placement="top"
                speaker={<Tooltip>Save or discard your changes first.</Tooltip>}
              >
                <span>
                  <SelectPicker
                    size="sm"
                    cleanable={false}
                    searchable={scenarios.length > 8}
                    disabled={isDirty}
                    data={scenarios.map((s) => ({ label: `${s.name} · ${periodLabel(s.startMonth, s.endMonth)}`, value: s.id }))}
                    value={selectedId}
                    onChange={(v) => setSelectedId(Number(v))}
                    style={{ width: 280 }}
                  />
                </span>
              </Whisper>
            ) : null}
            <button
              type="button"
              className="ppc-tod-btn ppc-tod-btn--sm"
              onClick={() => {
                createScenario.reset();
                setDetailsMode('create');
              }}
              disabled={isDirty}
              title={isDirty ? 'Save or discard your changes first' : undefined}
            >
              New scenario
              <i className="bi bi-plus-lg" aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </div>

      {!configured ? (
        <div className="ppc-tod-card__note">
          Scenarios are not set up on this server yet. Run <code>migrations/008_add_task_order_scenarios.sql</code> to turn them on.
        </div>
      ) : scenariosQuery.isLoading ? (
        <div className="ppc-sc__loading"><Loader content="Loading scenarios…" /></div>
      ) : scenarios.length === 0 ? (
        <div className="ppc-sc__empty">
          <i className="bi bi-sliders" aria-hidden="true" />
          <div>
            <div className="ppc-sc__empty-title">Try out staffing without touching the forecast</div>
            <div className="ppc-sc__empty-text">
              Pick a period, set how many hours each person gives this task order, and see how it lands against everyone&rsquo;s
              availability and the budget. You get a list of changes to hand to the discipline leads.
            </div>
          </div>
          <button type="button" className="ppc-tod-btn ppc-tod-btn--primary" onClick={() => setDetailsMode('create')}>
            New scenario
            <i className="bi bi-plus-lg" aria-hidden="true" />
          </button>
        </div>
      ) : !draft || scenarioQuery.isLoading ? (
        <div className="ppc-sc__loading"><Loader content="Loading scenario…" /></div>
      ) : (
        <div className="ppc-sc__body">
          <div className="ppc-sc__bar">
            <div className="ppc-sc__title">
              <span className="ppc-sc__name">{draft.name}</span>
              <span className="ppc-sc__period">
                <i className="bi bi-calendar-range" aria-hidden="true" />
                {periodLabel(draft.startMonth, draft.endMonth)} · {months.length} month{months.length === 1 ? '' : 's'}
              </span>
              {isDirty ? <span className="ppc-sc__dirty">Unsaved changes</span> : null}
              {!isDirty && saved?.updatedByName ? (
                <span className="ppc-sc__meta">Saved by {saved.updatedByName}</span>
              ) : null}
            </div>
            <div className="ppc-sc__actions">
              <button type="button" className="ppc-tod-btn ppc-tod-btn--sm" onClick={() => setDetailsMode('edit')}>
                Edit details
                <i className="bi bi-pencil" aria-hidden="true" />
              </button>
              <button type="button" className="ppc-tod-btn ppc-tod-btn--sm" onClick={requestDelete} disabled={isDirty}>
                Delete
                <i className="bi bi-trash" aria-hidden="true" />
              </button>
              {isDirty ? (
                <button type="button" className="ppc-tod-btn ppc-tod-btn--sm" onClick={discard} disabled={updateScenario.isPending}>
                  Discard
                </button>
              ) : null}
              <button
                type="button"
                className="ppc-tod-btn ppc-tod-btn--sm ppc-tod-btn--primary"
                onClick={save}
                disabled={!isDirty || updateScenario.isPending}
              >
                {updateScenario.isPending ? 'Saving…' : 'Save'}
                <i className="bi bi-check-lg" aria-hidden="true" />
              </button>
            </div>
          </div>
          {draft.notes ? <p className="ppc-sc__notes">{draft.notes}</p> : null}

          {contextQuery.error ? (
            <Message type="error" showIcon>Could not load availability: {errorText(contextQuery.error)}</Message>
          ) : null}

          <div className="ppc-sc-metrics">
            <div className="ppc-tod-metric ppc-sc-metric--wide">
              <div className="ppc-tod-metric__body">
                <div>
                  <p className="ppc-tod-metric__label">Hours available</p>
                  <p className="ppc-tod-metric__value ppc-tod-num">
                    {formatHours(totals.room)}<span className="ppc-sc-unit"> h</span>
                  </p>
                </div>
                <div className="ppc-tod-metric__foot">
                  {rows.length} people, after their other work · {formatFreeHours(totals.unused)} h still free
                </div>
              </div>
              <div className="ppc-sc-bars" aria-label="Available and planned hours by month">
                {months.map((ms) => {
                  const m = totals.byMonth[ms];
                  return (
                    <Whisper
                      key={ms}
                      placement="top"
                      trigger="hover"
                      speaker={(
                        <Tooltip>
                          {monthShort(ms)}: {formatHours(m.room)} h available, {formatHours(m.scenario)} h in scenario
                          {m.over > 0 ? `, ${formatHours(m.over)} h over someone’s availability` : ''}
                        </Tooltip>
                      )}
                    >
                      <div className="ppc-sc-bars__col">
                        <div className="ppc-sc-bars__track" style={{ height: `${(m.room / barMax) * 100}%` }} />
                        <div
                          className={`ppc-sc-bars__fill${m.over > 0 ? ' is-over' : ''}`}
                          style={{ height: `${(Math.min(m.scenario, barMax) / barMax) * 100}%` }}
                        />
                        <span className="ppc-sc-bars__label">{MONTHS_SHORT[Number(ms.slice(5, 7)) - 1][0]}</span>
                      </div>
                    </Whisper>
                  );
                })}
              </div>
            </div>

            <div className="ppc-tod-metric">
              <div className="ppc-tod-metric__body">
                <div>
                  <p className="ppc-tod-metric__label">In this scenario</p>
                  <p className="ppc-tod-metric__value ppc-tod-num">
                    {formatHours(totals.scenarioHours)}<span className="ppc-sc-unit"> h</span>
                  </p>
                </div>
                <div className={`ppc-tod-metric__foot${totals.over > 0 ? ' ppc-tod-metric__foot--bad' : ''}`}>
                  {totals.over > 0
                    ? `${formatHours(totals.over)} h over someone’s availability`
                    : `${formatMoney(totals.spend)} incl. ${formatMoneyCompact(totals.odc)} ODCs`}
                </div>
              </div>
            </div>

            <div className="ppc-tod-metric">
              <div className="ppc-tod-metric__body">
                <div>
                  <p className="ppc-tod-metric__label">Spend target for the period</p>
                  <InputNumber
                    className="ppc-sc-target"
                    prefix="$"
                    min={0}
                    step={1000}
                    value={draft.budgetTarget ?? (defaultTarget !== null ? Math.round(defaultTarget) : null)}
                    onChange={(v) => setDraft((d) => ({ ...d, budgetTarget: v === '' || v === null ? null : Number(v) }))}
                    formatter={(v) => (v === '' || v === null || v === undefined ? '' : Number(v).toLocaleString())}
                  />
                </div>
                <div className="ppc-tod-metric__foot">
                  {draft.budgetTarget !== null && draft.budgetTarget !== undefined ? (
                    <>
                      Custom target
                      {defaultTarget !== null ? (
                        <button type="button" className="ppc-sc-link" onClick={() => setDraft((d) => ({ ...d, budgetTarget: null }))}>
                          Use default ({formatMoneyCompact(defaultTarget)})
                        </button>
                      ) : null}
                    </>
                  ) : defaultTarget !== null ? (
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={(
                        <Tooltip>
                          Budget left ({formatMoney(budget - spentToDate)}) spread evenly over the months the task order has left,
                          times the months of this period inside it.
                        </Tooltip>
                      )}
                    >
                      <span className="ppc-sc-help">Default: even share of budget  <i className="bi bi-info-circle" aria-hidden="true" /></span>
                    </Whisper>
                  ) : (
                    'No budget set; enter a target'
                  )}
                </div>
              </div>
            </div>

            <div className="ppc-tod-metric">
              <div className="ppc-tod-metric__body">
                <div>
                  <p className="ppc-tod-metric__label">
                    {hoursToTarget !== null && hoursToTarget < 0 ? 'Hours over target' : 'Hours to hit target'}
                  </p>
                  <p className={`ppc-tod-metric__value ppc-tod-num${hoursToTarget !== null && hoursToTarget > totals.unused + 0.5 ? ' ppc-sc-text--bad' : ''}`}>
                    {hoursToTarget === null ? '—' : (
                      <>
                        {formatHours(Math.abs(hoursToTarget))}<span className="ppc-sc-unit"> h</span>
                      </>
                    )}
                  </p>
                </div>
                <div className="ppc-tod-metric__foot">
                  {hoursToTarget === null
                    ? 'Set a target to compare'
                    : hoursToTarget > totals.unused + 0.5
                      ? `Only ${formatFreeHours(totals.unused)} h free on this team`
                      : `At ${formatMoney(totals.blendedRate)}/h blended`}
                </div>
              </div>
            </div>
          </div>

          {budget === 0 ? (
            <div className="ppc-sc__finish">
              <i className="bi bi-flag" aria-hidden="true" />
              With this scenario the task order finishes at <strong>{formatMoney(finishTotal)}</strong> against a $0 budget.
            </div>
          ) : budget > 0 ? (
            <div className="ppc-sc__finish">
              <i className="bi bi-flag" aria-hidden="true" />
              With this scenario the task order finishes at <strong>{formatMoney(finishTotal)}</strong>, {' '}
              <span className={finishTotal > budget ? 'ppc-sc-text--bad' : undefined}>
                {((finishTotal / budget) * 100).toFixed(1)}% of the {formatMoneyCompact(budget)} budget
              </span>
              {finishTotal <= budget ? ` (${formatMoneyCompact(budget - finishTotal)} left).` : ` (${formatMoneyCompact(finishTotal - budget)} over).`}
            </div>
          ) : null}

          <div className="ppc-sc__toolbar">
            <div className="ppc-sc__toolbar-left">
              <button type="button" className="ppc-tod-btn ppc-tod-btn--sm" onClick={requestInsert} disabled={!rows.length}>
                Insert current forecast
                <i className="bi bi-box-arrow-in-down" aria-hidden="true" />
              </button>
              <button type="button" className="ppc-tod-btn ppc-tod-btn--sm" onClick={requestClear} disabled={totals.scenarioHours === 0}>
                Clear hours
                <i className="bi bi-eraser" aria-hidden="true" />
              </button>
              <SelectPicker
                size="sm"
                placeholder="Add person"
                data={addablePeople}
                value={null}
                onChange={(v) => addPerson(Number(v))}
                style={{ width: 200 }}
                virtualized
              />
            </div>
            <div className="ppc-sc-legend" aria-hidden="true">
              <span><span className="ppc-sc-legend__swatch ppc-sc-legend__swatch--over" /> Over availability</span>
              <span className="ppc-sc-legend__text">fcst = current forecast · free = available after other work</span>
            </div>
          </div>

          {contextQuery.isLoading && !context ? (
            <div className="ppc-sc__loading"><Loader content="Loading availability…" /></div>
          ) : rows.length === 0 ? (
            <div className="ppc-tod-card__note">Nobody is on this task order yet. Add people to start.</div>
          ) : (
            <div className="ppc-tod-tablewrap">
              <table className="ppc-tod-table ppc-sc-grid">
                <thead>
                  <tr>
                    <th scope="col" className="ppc-sc-sticky">Person</th>
                    {months.map((ms) => (
                      <th key={ms} scope="col">{monthShort(ms)}</th>
                    ))}
                    <th scope="col">Total</th>
                    <th scope="col">vs forecast</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.userId}>
                      <td className="ppc-sc-sticky">
                        <div className="ppc-sc-person">
                          <span>{r.name}</span>
                          {r.removable ? (
                            <button type="button" className="ppc-sc-remove" onClick={() => removePerson(r.userId)} aria-label={`Remove ${r.name}`} title="Remove from scenario">
                              <i className="bi bi-x" aria-hidden="true" />
                            </button>
                          ) : null}
                        </div>
                      </td>
                      {months.map((ms) => {
                        const c = r.cells[ms];
                        const isOver = c.scenario > c.room + 0.01;
                        const free = c.room - c.scenario;
                        const flashFillRun = flashFillRunsByUserId.get(r.userId)?.get(ms);
                        const flashFillRunKey = flashFillRun ? getFlashFillRunKey(r.userId, flashFillRun) : undefined;
                        const previewFree = flashFillRun ? c.room - flashFillRun.value : 0;
                        const input = (
                          <SumInput
                            className="ppc-sc-input ppc-tod-num"
                            value={c.scenario}
                            placeholder="0"
                            onCommit={(v) => setCell(r.userId, ms, v)}
                            onKeyDown={(e) => {
                              if (isFlashFillShortcut(e)) {
                                e.preventDefault();
                                applyFlashFill(r, uniqueFlashFillRuns(flashFillRunsByUserId.get(r.userId)));
                              }
                            }}
                            aria-label={`${r.name} hours in ${monthShort(ms)}`}
                          />
                        );
                        return (
                          <td
                            key={ms}
                            data-flash-fill-run={flashFillRunKey}
                            className={`ppc-sc-cell${isOver ? ' is-over' : ''}${Math.abs(c.scenario - c.current) >= 0.5 ? ' is-changed' : ''}`}
                          >
                            <Whisper
                              placement="top"
                              trigger="hover"
                              delayOpen={300}
                              speaker={(
                                <Tooltip className="ppc-sc-tip">
                                  <strong>{r.name}, {monthShort(ms)}</strong>
                                  <br />Capacity {formatHours(c.capacity)} h{c.timeOff > 0 ? ` (after ${formatHours(c.timeOff)} h time off)` : ''}
                                  <br />Other work {formatHours(c.other)} h
                                  <br />Available here {formatHours(c.room)} h
                                  <br />Current forecast {formatHours(c.current)} h
                                  {c.rate > 0 ? <><br />Rate {formatMoney(c.rate)}/h</> : <><br />No rate in effect</>}
                                </Tooltip>
                              )}
                            >
                              <div className="ppc-sc-cell__inner">
                                <div className="ppc-sc-cell__entry">
                                  {flashFillRun && flashFillRun.months[0] === ms ? (
                                    <FlashFillButton
                                      runKey={flashFillRunKey}
                                      run={flashFillRun}
                                      onAccept={() => applyFlashFill(r, [flashFillRun])}
                                    />
                                  ) : null}
                                  {flashFillRun ? <FlashFillGhost text={formatHours(flashFillRun.value)}>{input}</FlashFillGhost> : input}
                                </div>
                                <span className="ppc-sc-cell__meta ppc-tod-num">
                                  <span className={flashFillRun ? 'ppc-flash-fill-preview-hide' : undefined}>
                                    fcst {formatHours(c.current)} · {isOver ? <span className="ppc-sc-text--bad">{formatOverHours(free)} over</span> : `free ${formatFreeHours(free)}`}
                                  </span>
                                  {flashFillRun ? (
                                    <span className="ppc-flash-fill-preview-only">
                                      fcst {formatHours(c.current)} · {previewFree < -0.01 ? `${formatOverHours(previewFree)} over` : `free ${formatFreeHours(previewFree)}`}
                                    </span>
                                  ) : null}
                                </span>
                              </div>
                            </Whisper>
                          </td>
                        );
                      })}
                      <td className="ppc-tod-num ppc-sc-total">
                        {formatHours(r.scenarioHours)}
                        <div className="ppc-sc-sub">{formatMoneyCompact(r.scenarioDollars)}</div>
                      </td>
                      <td className={`ppc-tod-num ${r.scenarioHours - r.currentHours > 0.25 ? 'ppc-sc-text--up' : r.scenarioHours - r.currentHours < -0.25 ? 'ppc-sc-text--down' : 'ppc-tod-table__muted'}`}>
                        {Math.abs(r.scenarioHours - r.currentHours) < 0.25 ? 'Same' : `${signed(r.scenarioHours - r.currentHours)} h`}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="ppc-sc-sticky">Scenario</td>
                    {months.map((ms) => (
                      <td key={ms} className="ppc-tod-num">{formatHours(totals.byMonth[ms].scenario)}</td>
                    ))}
                    <td className="ppc-tod-num">{formatHours(totals.scenarioHours)}</td>
                    <td className="ppc-tod-num">{signed(totals.scenarioHours - totals.currentHours)} h</td>
                  </tr>
                  <tr className="ppc-sc-foot-sub">
                    <td className="ppc-sc-sticky">Current forecast</td>
                    {months.map((ms) => (
                      <td key={ms} className="ppc-tod-num">{formatHours(totals.byMonth[ms].current)}</td>
                    ))}
                    <td className="ppc-tod-num">{formatHours(totals.currentHours)}</td>
                    <td />
                  </tr>
                  <tr className="ppc-sc-foot-sub">
                    <td className="ppc-sc-sticky">Available</td>
                    {months.map((ms) => (
                      <td key={ms} className="ppc-tod-num">{formatHours(totals.byMonth[ms].room)}</td>
                    ))}
                    <td className="ppc-tod-num">{formatHours(totals.room)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <div className="ppc-sc-howto">
            <div className="ppc-sc-howto__head">
              <div>
                <h3 className="ppc-sc-howto__title">How to get there</h3>
                <div className="ppc-sc-howto__sub">
                  {totals.scenarioHours === 0
                    ? 'Enter hours or insert the current forecast to see what the leads would need to change.'
                    : instructions.length === 0
                      ? 'This scenario matches the current forecast. Nothing for the leads to change.'
                      : `${instructionPeople} ${instructionPeople === 1 ? 'person' : 'people'} · +${formatHours(addedTotal)} h added, −${formatHours(removedTotal)} h removed. Hand these to the discipline leads; the forecast itself is unchanged.`}
                </div>
              </div>
              {totals.scenarioHours > 0 && instructions.length > 0 ? (
                <div className="ppc-sc-howto__tools">
                  <div className="ppc-tod-segment" role="group" aria-label="Group instructions by">
                    <button type="button" className={`ppc-tod-btn ppc-tod-btn--sm${groupBy === 'discipline' ? ' is-active' : ''}`} onClick={() => setGroupBy('discipline')} aria-pressed={groupBy === 'discipline'}>
                      By discipline
                    </button>
                    <button type="button" className={`ppc-tod-btn ppc-tod-btn--sm${groupBy === 'person' ? ' is-active' : ''}`} onClick={() => setGroupBy('person')} aria-pressed={groupBy === 'person'}>
                      By person
                    </button>
                  </div>
                  <button type="button" className="ppc-tod-btn ppc-tod-btn--sm" onClick={copyInstructions}>
                    Copy
                    <i className="bi bi-clipboard" aria-hidden="true" />
                  </button>
                </div>
              ) : null}
            </div>

            {totals.scenarioHours > 0 && instructions.length > 0 ? (
              <div className="ppc-sc-howto__groups">
                {instructionGroups.map((g) => (
                  <div key={g.key} className="ppc-sc-group">
                    <div className="ppc-sc-group__head">
                      <span className="ppc-sc-group__title">{g.title}</span>
                      {groupBy === 'discipline' ? (
                        <span className="ppc-sc-group__side">
                          <span className="ppc-sc-group__lead">
                            <i className="bi bi-person" aria-hidden="true" />
                            {g.key.endsWith(':none') ? 'PM to place' : g.lead ? `Lead: ${g.lead}` : 'No lead set'}
                          </span>
                          {emailContactsFor(g.items[0].taskId).length > 0 ? (
                            <button
                              type="button"
                              className="ppc-tod-btn ppc-tod-btn--sm"
                              onClick={() => openEmail(g.items[0].taskId, g.items)}
                              title={`Email all ${g.title} instructions`}
                            >
                              Email lead
                              <i className="bi bi-envelope" aria-hidden="true" />
                            </button>
                          ) : null}
                        </span>
                      ) : null}
                    </div>
                    <ul className="ppc-sc-group__list">
                      {g.items.map((item) => {
                        const tone = item.months.every((m) => m.hours > 0) ? 'up' : item.months.every((m) => m.hours < 0) ? 'down' : 'mixed';
                        return (
                          <li key={`${item.userId}|${item.taskId}`} className="ppc-sc-item">
                            <span className={`ppc-sc-item__icon ppc-sc-item__icon--${tone}`} aria-hidden="true">
                              <i className={`bi ${tone === 'up' ? 'bi-arrow-up' : tone === 'down' ? 'bi-arrow-down' : 'bi-arrow-left-right'}`} />
                            </span>
                            <div className="ppc-sc-item__body">
                              <div className="ppc-sc-item__text">{describeInstruction(item, disciplineLabel(item.taskId))}</div>
                              <div className="ppc-sc-item__months ppc-tod-num">{monthDetail(item)}</div>
                            </div>
                            <div className="ppc-sc-item__actions">
                              <button
                                type="button"
                                className="ppc-sc-item__action"
                                onClick={() => copyText(instructionLine(item), 'Instruction copied.')}
                                title="Copy this instruction"
                                aria-label="Copy this instruction"
                              >
                                <i className="bi bi-clipboard" aria-hidden="true" />
                              </button>
                              {emailContactsFor(item.taskId).length > 0 ? (
                                <button
                                  type="button"
                                  className="ppc-sc-item__action"
                                  onClick={() => openEmail(item.taskId, [item])}
                                  title={`Email the ${disciplineLabel(item.taskId)} lead about this`}
                                  aria-label={`Email the ${disciplineLabel(item.taskId)} lead about this`}
                                >
                                  <i className="bi bi-envelope" aria-hidden="true" />
                                </button>
                              ) : null}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      )}

      <ScenarioDetailsModal
        open={detailsMode !== null}
        title={detailsMode === 'create' ? 'New scenario' : 'Scenario details'}
        initial={detailsMode === 'create' ? newScenarioDefaults : draft}
        monthOptions={detailsMode === 'edit' && draft && draft.startMonth < currentMonth
          ? [...monthsBetween(draft.startMonth, addMonths(currentMonth, -1)).map((ms) => ({ label: monthShort(ms), value: ms })), ...monthOptions]
          : monthOptions}
        periodWarning={detailsMode === 'edit' ? draft : null}
        pending={createScenario.isPending}
        error={detailsMode === 'create' ? createScenario.error : null}
        onCancel={() => setDetailsMode(null)}
        onSubmit={submitDetails}
      />
      <ConfirmModal confirm={confirm} onClose={() => !confirm?.pending && setConfirm(null)} />
      <LeaveUnsavedModal
        open={leaveBlocker.state === 'blocked'}
        saving={savingToLeave}
        onSaveAndLeave={saveAndLeave}
        onLeave={() => leaveBlocker.proceed?.()}
        onStay={() => leaveBlocker.reset?.()}
      />
      <EmailLeadModal
        email={emailRequest}
        pending={emailLead.isPending}
        error={emailLead.error}
        onCancel={() => setEmailRequest(null)}
        onSend={sendEmail}
      />
    </section>
  );
}
